import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {Absence} from '../models/absence.model';
import {NotificationService} from './notification.service';
import {DatevAuthStatusService} from "./datev-auth-status.service";
import {extractHttpErrorMessage} from '../shared/http-error.util';

export interface SickLeaveRequest {
    employeeNumber: number;
    start_work_incapacity: string;
    end_work_incapacity_au: string;
    follow_up_certification: boolean;
    mit_attest: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SickLeaveService {
    private apiUrl = `${environment.apiUrl}/datev`;

    constructor(
        private http: HttpClient,
        private notificationService: NotificationService,
    ) {
    }

    /**
     * Erstellt eine Krankmeldung direkt als Abwesenheit bei DATEV (POST /eau, aktuelles
     * Backend-Modell). Anders als die übrigen Methoden dieser Klasse (noch auf dem alten
     * /datev/eau-requests-Zwischenschritt) gibt es hier kein {success, message}-Objekt mehr,
     * nur die neu erstellte AbsenceId - Erfolgs-Feedback übernimmt daher der Aufrufer.
     */
    createSickLeave(request: SickLeaveRequest): Observable<number> {
        return this.http.post<number>(`${environment.apiUrl}/eau`, request)
            .pipe(
                tap({
                    error: error => {
                        this.handleError(error);
                    }
                })
            );
    }

    /**
     * Liest die Liste der Krankmeldungen (Abwesenheiten) über das aktuelle Backend-Modell
     * (GET /eau/all, EauController). Liefert Abwesenheit[] - nicht mehr die alte, inzwischen
     * entfernte {success, data}-Zwischenschicht unter /datev/eau-requests.
     */
    getSickLeaves(): Observable<Absence[]> {
        return this.http.get<Absence[]>(`${environment.apiUrl}/eau/all`)
            .pipe(
                tap({
                    error: error => {
                        this.handleError(error);
                    }
                })
            );
    }

    /**
     * Holt das Ergebnis des DATEV-Jobs ab (POST /eau/:absenceId/retrieve-job-result,
     * EauController.retrieveJobResult). Nur nutzbar, wenn der zugehörige Cron-Job
     * (cron_settings 'job_result_retrieval') für diesen Schritt deaktiviert ist - sonst erledigt
     * das der Cron automatisch. Siehe submitEau() zum Hintergrund der manuellen Auslösung.
     */
    retrieveJobResult(absenceId: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/eau/${absenceId}/retrieve-job-result`, {})
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleActionError(error))
            );
    }

    /**
     * Legt die eAU bei DATEV an (POST /eau/:absenceId/create-eau, EauController.createEau). Nur
     * nutzbar, wenn der zugehörige Cron-Job (cron_settings 'eau_creation') für diesen Schritt
     * deaktiviert ist - sonst erledigt das der Cron automatisch. Siehe submitEau() zum
     * Hintergrund der manuellen Auslösung.
     */
    createEau(absenceId: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/eau/${absenceId}/create-eau`, {})
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleActionError(error))
            );
    }

    /**
     * Reicht die Krankmeldung (eAU) bei der Krankenkasse ein (POST /eau/:absenceId/submit,
     * EauController). Nur nutzbar, wenn der zugehörige Cron-Job für diesen Schritt deaktiviert
     * ist - sonst erledigt das der Cron automatisch. Rückgabe ist ein undokumentiertes Objekt,
     * der Aufrufer reagiert nur auf Erfolg/Fehler, nicht auf die Form der Antwort.
     */
    submitEau(absenceId: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/eau/${absenceId}/submit`, {})
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleActionError(error))
            );
    }

    /**
     * Holt die Rückmeldung der Krankenkasse ab (POST /eau/:absenceId/retrieve-feedback,
     * EauController). Siehe submitEau() zum Hintergrund der manuellen Auslösung.
     */
    retrieveFeedback(absenceId: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/eau/${absenceId}/retrieve-feedback`, {})
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleActionError(error))
            );
    }

    /**
     * Storniert die bereits eingereichte Krankmeldung bei der Krankenkasse
     * (POST /eau/:absenceId/cancel-feedback, EauController). Siehe submitEau() zum Hintergrund
     * der manuellen Auslösung.
     */
    cancelFeedback(absenceId: number): Observable<void> {
        return this.http.post<void>(`${environment.apiUrl}/eau/${absenceId}/cancel-feedback`, {})
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleActionError(error))
            );
    }

    private handleError(error: HttpErrorResponse): void {
        this.notificationService.show(extractHttpErrorMessage(error), 'error');
    }

    /**
     * Wie handleError(), gibt den Fehler aber zusätzlich als lesbare Nachricht (statt des rohen
     * HttpErrorResponse) weiter, damit Aufrufer (z.B. das pro-Zeile actionError()-Signal der
     * manuellen EAU-Aktionen) ihn direkt anzeigen können, ohne "[object Object]" zu rendern.
     */
    private handleActionError(error: HttpErrorResponse): Observable<never> {
        const message = extractHttpErrorMessage(error);
        this.notificationService.show(message, 'error');
        return throwError(() => message);
    }
}